/**
 * La chaîne quête → faits → passages du cours, côté écran. Pure.
 *
 * tc_learn rend les identifiants des faits que la génération a reçus
 * (`steps[0].fact_ids`) ; on les résout ici dans l'unité `neighborhood` de
 * l'objectif (tc_embed_be), qui porte les faits propres sous leur identifiant
 * local (`t1`) et ceux des voisins sous un identifiant composite
 * (`<8 hex>:t1`). Chaque fait garde l'ordinal du passage du cours dont il a
 * été extrait (`chunk`) : c'est le dernier maillon.
 */
import type { KnowledgeUnit } from "../../api/knowledge/knowledge";

export interface ResolvedFact {
  id: string;
  kind: "terme" | "relation" | "étape" | "date" | "idée fausse" | "catégorie";
  text: string;
  /** Ordinal du passage source (0-based), absent si le fait n'en dit rien. */
  chunk: number | null;
  /** Le fait que la question interroge (ancre du QCM v3). */
  anchor: boolean;
}

/** `portée:version:id` → `id` (l'id peut lui-même contenir `:`). */
export const anchorFactId = (anchorKey: string | null | undefined): string | null => {
  if (!anchorKey) return null;
  const parts = anchorKey.split(":");
  return parts.length >= 3 ? parts.slice(2).join(":") : null;
};

const label = (unit: KnowledgeUnit, termId: string): string =>
  unit.terms.find((t) => t.id === termId)?.term ?? termId;

/** Tous les faits de l'unité, rendus en une ligne (même rendu que le prompt). */
const allFacts = (unit: KnowledgeUnit): Array<Omit<ResolvedFact, "anchor">> => [
  ...unit.terms.map((t) => ({
    id: t.id,
    kind: "terme" as const,
    text: t.definition ? `${t.term} — ${t.definition}` : t.term,
    chunk: t.chunk ?? null,
  })),
  ...unit.relations.map((r) => ({
    id: r.id,
    kind: "relation" as const,
    text: `${label(unit, r.from)} — ${r.label} → ${label(unit, r.to)}`,
    chunk: r.chunk ?? null,
  })),
  ...unit.steps.map((s) => ({ id: s.id, kind: "étape" as const, text: `${s.order}. ${s.text}`, chunk: s.chunk ?? null })),
  ...unit.dates.map((d) => ({
    id: d.id,
    kind: "date" as const,
    text: d.event ? `${d.when} — ${d.event}` : d.when,
    chunk: d.chunk ?? null,
  })),
  ...unit.misconceptions.map((m) => ({
    id: m.id,
    kind: "idée fausse" as const,
    text: m.correction ? `${m.claim} → ${m.correction}` : m.claim,
    chunk: m.chunk ?? null,
  })),
  ...unit.categories.map((c) => ({
    id: c.id,
    kind: "catégorie" as const,
    text: `${c.name} : ${c.members.map((m) => label(unit, m)).join(", ")}`,
    chunk: c.chunk ?? null,
  })),
];

export interface ResolvedChain {
  facts: ResolvedFact[];
  /** Identifiants que l'unité actuelle ne connaît plus (ré-extraction, fait retiré). */
  missing: string[];
  /** Ordinaux des passages cités, dans l'ordre du cours. */
  chunks: number[];
}

/** Résout les faits de la quête dans l'unité ; l'ancre passe en tête. */
export const resolveFacts = (
  unit: KnowledgeUnit | null,
  factIds: string[],
  anchorKey?: string | null,
): ResolvedChain => {
  const byId = new Map((unit ? allFacts(unit) : []).map((f) => [f.id, f]));
  const anchor = anchorFactId(anchorKey);
  const facts: ResolvedFact[] = [];
  const missing: string[] = [];
  factIds.forEach((id) => {
    const fact = byId.get(id);
    if (fact) facts.push({ ...fact, anchor: id === anchor });
    else missing.push(id);
  });
  facts.sort((a, b) => Number(b.anchor) - Number(a.anchor));
  const chunks = Array.from(new Set(facts.map((f) => f.chunk).filter((c): c is number => c !== null))).sort(
    (a, b) => a - b,
  );
  return { facts, missing, chunks };
};

/**
 * La base de faits a-t-elle changé depuis la génération ? Les identifiants de
 * faits sont POSITIONNELS : après une ré-extraction, `t3` peut désigner un
 * autre fait. L'écran le dit au lieu de montrer un faux maillon.
 */
export const knowledgeChanged = (questVersion: string, sourceHash: string | null | undefined): boolean =>
  Boolean(questVersion) && Boolean(sourceHash) && !String(sourceHash).startsWith(questVersion);
